import { useState } from "react";
import { useHeroImageModalStore } from "../../../js/stores/hero-image-modal-store";
import { Dialog, DialogContent } from "../shared/modal";
import { HeroImageModal } from "./hero-image-modal";
import { toPoint } from "../shared/focal-point-picker";

/** Mirrors profile.GetCoverImageUrl(1200, 675) in Views/MyProfile.cshtml (MemberProfileExtensions). */
const HERO_CROP = "?rmode=crop&width=1200&height=675";

/**
 * Cover image widget (T212647). Only rendered for members with an active influencer badge —
 * the gate is in Views/MyProfile.cshtml, via IMemberBadgeService.
 *
 * With no cover uploaded the preview shows the SAME branded default the public profile
 * falls back to, so the member sees what visitors will actually see rather than an empty
 * box. Removing a cover therefore visibly reverts to that default rather than to nothing.
 */
export const HeroImageWidget = ({
  heroUrl,
  heroPosition,
  coverKey,
  coverSourceUrl,
  coverFocalLeft,
  coverFocalTop,
  altText,
  maxFileSizeBytes,
}) => {
  const isModalOpen = useHeroImageModalStore((state) => state.isModalOpen);
  const openModal = useHeroImageModalStore((state) => state.openModal);
  const closeModal = useHeroImageModalStore((state) => state.closeModal);
  const updateIsModalOpen = useHeroImageModalStore(
    (state) => state.updateIsModalOpen
  );

  const [hero, setHero] = useState(heroUrl);
  const [previewAngle, setPreviewAngle] = useState(0);
  // "upload" a new cover, or "adjust" the framing of the one already there.
  const [mode, setMode] = useState("upload");

  const hasHero = !!hero;
  const canAdjust = hasHero && !!coverKey && !!coverSourceUrl;

  const open = (nextMode) => {
    setMode(nextMode);
    openModal();
  };

  return (
    <div className="hero-image__wrapper">
      <div
        className={`hero-image__preview ${hasHero ? "" : "hero-image__preview--default"}`}
        data-testid="cover-image-preview"
        data-cover-default={hasHero ? "false" : "true"}
        role="img"
        aria-label={altText || "Cover image"}
        style={{
          backgroundImage: hasHero ? `url(${hero})` : undefined,
          // The preview box is not the 1200x675 the URL is cropped to at every width, so it
          // positions on the same focal point the crop does.
          backgroundPosition: hasHero && heroPosition ? heroPosition : undefined,
        }}
      >
        {!hasHero && (
          <span className="hero-image__placeholder">
            Default Valspar cover
          </span>
        )}
      </div>
      
      <button
        className="hero-image__popup-button"
        data-testid="cover-image-upload-open"
        onClick={() => open("upload")}
      >
        Upload cover image
      </button>

      {canAdjust && (
        <button
          className="hero-image__popup-button"
          data-testid="cover-image-adjust-framing"
          onClick={() => open("adjust")}
        >
          Adjust framing
        </button>
      )}

      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          onClose={() => updateIsModalOpen(false)}
          onOpenChange={updateIsModalOpen}
        >
          <DialogContent>
            <HeroImageModal
              closeModal={closeModal}
              altText={altText}
              maxFileSizeBytes={maxFileSizeBytes}
              existing={
                mode === "adjust" && canAdjust
                  ? {
                      mediaKey: coverKey,
                      src: coverSourceUrl,
                      point: toPoint(coverFocalLeft, coverFocalTop),
                    }
                  : null
              }
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
